<template>
  <div>
    <div class="mb-6 text-3xl font-light lg:text-5xl">
      {{ $t('inquiryForm.stepThree.title') }}
    </div>
    <div class="mb-[6px] text-base text-neutral-500 lg:mb-3">
      {{ $t('inquiryForm.stepThree.description') }}
    </div>

    <div class="grid-default gap-y-6 lg:!gap-x-4 lg:gap-y-8">
      <FormInput
        id="Name"
        v-model="state.Name"
        name="Name"
        class-field="col-span-6"
        :placeholder="$t('inquiryForm.stepThree.form.name.placeholder')"
        :label="$t('inquiryForm.stepThree.form.name.label')"
        :isError="v$.Name.$error"
        @change="v$.Name.$touch"
      />
      <FormInput
        id="Surname"
        v-model="state.Surname"
        name="Surname"
        class-field="col-span-6"
        :placeholder="$t('inquiryForm.stepThree.form.surname.placeholder')"
        :label="$t('inquiryForm.stepThree.form.surname.label')"
        :isError="v$.Surname.$error"
        @change="v$.Surname.$touch"
      />
      <FormInput
        id="Telephone"
        v-model="state.Telephone"
        name="Telephone"
        class-field="col-span-6"
        type="number"
        :placeholder="$t('inquiryForm.stepThree.form.telephone.placeholder')"
        :label="$t('inquiryForm.stepThree.form.telephone.label')"
        :isError="v$.Telephone.$error"
        @change="v$.Telephone.$touch"
      />
      <FormInput
        id="Email"
        v-model="state.Email"
        name="Email"
        class-field="col-span-6"
        :placeholder="$t('inquiryForm.stepThree.form.email.placeholder')"
        :label="$t('inquiryForm.stepThree.form.email.label')"
        :error-message="$t('inquiryForm.stepThree.form.email.error')"
        :isError="v$.Email.$error"
        @change="v$.Email.$touch"
      />
      <FormInput
        id="CompanyName"
        v-model="state.CompanyName"
        name="CompanyName"
        class-field="col-span-6"
        :placeholder="$t('inquiryForm.stepThree.form.company.placeholder')"
        :label="$t('inquiryForm.stepThree.form.company.label')"
      />
      <FormInput
        id="TaxNumber"
        v-model="state.TaxNumber"
        name="TaxNumber"
        type="text"
        class-field="col-span-6"
        :placeholder="$t('inquiryForm.stepThree.form.tax.placeholder')"
        :label="$t('inquiryForm.stepThree.form.tax.label')"
      />
      <div class="col-span-6 lg:col-span-12">
        <FormCheckbox
          id="PrivacyAccepted"
          v-model="state.PrivacyAccepted"
          name="PrivacyAccepted"
          :label="$t('inquiryForm.stepThree.form.privacy.label')"
          :error-message="$t('inquiryForm.requiredField')"
          :isError="v$.PrivacyAccepted.$error"
          @change="v$.PrivacyAccepted.$touch"
        />
      </div>
      <div class="col-span-6 lg:col-span-12">
        <FormCheckbox
          id="Subscribed"
          v-model="state.Subscribed"
          name="Subscribed"
          :label="$t('inquiryForm.stepThree.form.subscribe.label')"
        />
      </div>
      <div class="cols-span-6 lg:col-span-12">
        <FormField :isError="v$.recaptchaValue.$error">
          <ClientOnly>
            <RecaptchaV2
              @load-callback="onRecaptchaVerify"
              @expired-callback="onRecaptchaExpired"
            />
          </ClientOnly>
        </FormField>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import useVuelidate from '@vuelidate/core'
import { required, email } from '@vuelidate/validators'
import type { InquiryUserDetails } from '~/types/inquiry'
import { RecaptchaV2 } from 'vue3-recaptcha-v2'

// Define props and emit
const emit = defineEmits(['update:modelValue', 'form:validated', 'form:invalid'])
const props = defineProps({
  modelValue: {
    type: Object as PropType<InquiryUserDetails>,
    required: true
  }
})

// Form state
const state = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

// Vuelidate rules
const rules = {
  Name: { required },
  Surname: { required },
  Telephone: { required },
  Email: { required, email },
  PrivacyAccepted: { required, isTrue: (value: boolean) => value === true },
  recaptchaValue: { required }
}

const v$ = useVuelidate(rules, state)

function onRecaptchaVerify(token: string) {
  state.value.recaptchaValue = token
}

function onRecaptchaExpired() {
  state.value.recaptchaValue = ''
}

// Watch form validation
watch(
  () => v$.value.$invalid,
  () => {
    if (!v$.value.$invalid) {
      emit('form:validated')
    } else {
      emit('form:invalid')
    }
  }
)
</script>
